import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  PaperPlaneTilt,
  Spinner,
  TrayArrowDown,
  TrayArrowUp,
  X,
} from "@phosphor-icons/react";
import { config } from "../config";
import { Turnstile } from "./Turnstile";

const WORKER_URL = "https://design-chef-bot.wr7thful.workers.dev/feedback";

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const turnstileEnabled = config.turnstileSiteKey !== "";

  const handleClose = useCallback(() => {
    setOpen(false);
    setSent(false);
    setShaking(false);
    setError(false);
    setTurnstileToken(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim()) {
        setShaking(true);
        window.setTimeout(() => setShaking(false), 500);
        textareaRef.current?.focus();
        return;
      }
      if (turnstileEnabled && !turnstileToken) {
        setShaking(true);
        window.setTimeout(() => setShaking(false), 500);
        return;
      }

      if (honeypot.trim()) {
        setSent(true);
        setSending(false);
        setMessage("");
        setName("");
        return;
      }

      setError(false);
      setSending(true);

      try {
        const res = await fetch(WORKER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project: "yay",
            message: message.trim(),
            name: name.trim() || undefined,
            page: location.host + location.pathname,
            ...(turnstileEnabled ? { turnstileToken } : {}),
          }),
        });

        const data = (await res.json()) as { ok?: boolean };

        if (res.ok && data.ok) {
          setSent(true);
          window.setTimeout(() => {
            setSent(false);
            setOpen(false);
            setMessage("");
            setName("");
          }, 2000);
        } else {
          setError(true);
        }
      } catch {
        setError(true);
      } finally {
        setSending(false);
      }
    },
    [message, name, honeypot, turnstileEnabled, turnstileToken],
  );

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      setOpen(true);
    }
  }, [open, handleClose]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    setShaking(false);
    setError(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        handleClose();
      }
    }

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, handleClose]);

  return (
    <div className="top-bar-feedback" ref={rootRef}>
      {open && (
        <div className="feedback-card">
          {sent ? (
            <div className="feedback-success">
              <CheckCircle size={40} weight="fill" />
              <span>Thanks!</span>
            </div>
          ) : (
            <>
              <div className="feedback-header">
                <span>Feedback</span>
                <button
                  type="button"
                  className="feedback-close"
                  onClick={handleClose}
                  aria-label="Close feedback"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
              <form className="feedback-body" onSubmit={handleSubmit}>
                <textarea
                  ref={textareaRef}
                  className={`feedback-textarea${shaking ? " feedback-shake" : ""}${error ? " feedback-error" : ""}`}
                  placeholder={
                    shaking
                      ? "Say something first"
                      : error
                        ? "Couldn't send. Try again?"
                        : "Your feedback..."
                  }
                  rows={4}
                  value={message}
                  onChange={handleChange}
                  maxLength={2000}
                  autoFocus
                  disabled={sending}
                />
                <input
                  className="feedback-name"
                  type="text"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  disabled={sending}
                />
                <input
                  type="text"
                  className="contact-hp"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                {turnstileEnabled && (
                  <Turnstile siteKey={config.turnstileSiteKey} onToken={setTurnstileToken} />
                )}
                <button
                  type="submit"
                  className="feedback-submit"
                  disabled={sending || (turnstileEnabled && !turnstileToken)}
                >
                  {sending ? (
                    <>
                      <Spinner
                        size={16}
                        weight="bold"
                        className="feedback-spinner"
                      />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={16} weight="fill" />
                      <span>Send Feedback</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className={`collapse-toggle${open ? " chat-open" : ""}`}
        onClick={handleToggle}
        aria-label={open ? "Close feedback" : "Open feedback"}
        aria-expanded={open}
        title="Feedback"
      >
        {open ? (
          <TrayArrowUp size={16} weight="fill" />
        ) : (
          <TrayArrowDown size={16} weight="regular" />
        )}
      </button>
    </div>
  );
}
